import ColorInputField from "@/components/common/ColorInputField";
import SelectField from "@/components/common/SelectField";
import { X } from "lucide-react";
import { Button } from "primereact/button";
import { useEffect, useRef } from "react";
import {
  POST_PROCESSING_TYPE_FIELD_OPTIONS,
  POST_PROCESSING_TYPE_NULL_VALUE,
  PostProcessingConfig,
  PostProcessingType,
} from "./form-config";

interface PostProcessingUpdate {
  postProcessingType: PostProcessingType;
  postProcessingConfig: PostProcessingConfig;
}

interface Props {
  postProcessingType: PostProcessingType;
  postProcessingConfig: PostProcessingConfig;
  onChange: (update: PostProcessingUpdate) => void;
}

export default function PostProcessingFields({
  postProcessingType,
  postProcessingConfig,
  onChange,
}: Props) {
  const colorUpdateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const latestPostProcessingConfigRef =
    useRef<PostProcessingConfig>(postProcessingConfig);

  useEffect(() => {
    latestPostProcessingConfigRef.current = postProcessingConfig;
  }, [postProcessingConfig]);

  useEffect(() => {
    return () => {
      if (colorUpdateTimeoutRef.current) {
        clearTimeout(colorUpdateTimeoutRef.current);
      }
    };
  }, []);

  const clearPendingColorUpdate = () => {
    if (colorUpdateTimeoutRef.current) {
      clearTimeout(colorUpdateTimeoutRef.current);
      colorUpdateTimeoutRef.current = null;
    }
  };

  const handleTypeChange = (newType: PostProcessingType) => {
    if (newType === postProcessingType) {
      return;
    }

    clearPendingColorUpdate();
    onChange({
      postProcessingType: newType,
      postProcessingConfig:
        newType === "color_removal_and_fill"
          ? (postProcessingConfig ?? {
              removeColor: "#ffffff",
              fillColor: null,
            })
          : null,
    });
  };

  const handleColorChange = (
    key: "removeColor" | "fillColor",
    color: string,
  ) => {
    const currentConfig = latestPostProcessingConfigRef.current ?? {
      removeColor: "#ffffff",
      fillColor: null,
    };

    if (currentConfig[key] === color) {
      return;
    }

    const nextConfig = {
      ...currentConfig,
      [key]: color,
    };

    latestPostProcessingConfigRef.current = nextConfig;
    clearPendingColorUpdate();
    colorUpdateTimeoutRef.current = setTimeout(() => {
      onChange({
        postProcessingType,
        postProcessingConfig: nextConfig,
      });
      colorUpdateTimeoutRef.current = null;
    }, 200);
  };

  const handleClearFillColor = () => {
    clearPendingColorUpdate();

    if (!latestPostProcessingConfigRef.current?.fillColor) {
      return;
    }

    const nextConfig = {
      removeColor:
        latestPostProcessingConfigRef.current?.removeColor ?? "#ffffff",
      fillColor: null,
    };

    latestPostProcessingConfigRef.current = nextConfig;
    onChange({
      postProcessingType,
      postProcessingConfig: nextConfig,
    });
  };

  return (
    <>
      <SelectField
        className="flex-col items-start w-full col-span-2 gap-1"
        inputClassName="h-9 px-2"
        label="Post Processing Type"
        value={postProcessingType ?? POST_PROCESSING_TYPE_NULL_VALUE}
        options={POST_PROCESSING_TYPE_FIELD_OPTIONS}
        onChange={(event) =>
          handleTypeChange(
            (event.target.value === POST_PROCESSING_TYPE_NULL_VALUE
              ? null
              : event.target.value) as PostProcessingType,
          )
        }
      />
      {postProcessingType === "color_removal_and_fill" && (
        <>
          <ColorInputField
            className="flex-col items-start w-full gap-1"
            label="Remove Color"
            name="removeColor"
            defaultValue={postProcessingConfig?.removeColor}
            onChange={(color) => handleColorChange("removeColor", color)}
          />
          <div className="relative flex items-end w-full gap-2">
            <ColorInputField
              className="flex-col items-start flex-1 gap-1"
              label="Fill Color (Optional)"
              name="fillColor"
              defaultValue={postProcessingConfig?.fillColor ?? undefined}
              onChange={(color) => handleColorChange("fillColor", color)}
            />
            {postProcessingConfig?.fillColor && (
              <div className={`absolute top-8.75 right-1.5 group-hover:block`}>
                <Button
                  severity="danger"
                  type="button"
                  style={{ padding: 0, width: 20, height: 20 }}
                  icon={<X size={14} />}
                  onClick={handleClearFillColor}
                />
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}
